import { ApiService } from './api_service';

// Coupon validation and management service
export class CouponService {
  /**
   * Validate and apply a coupon
   * @param {string} couponCode - The coupon code to validate
   * @param {number} subtotal - The subtotal amount before discount
   * @param {string} userId - Current user ID
   * @param {string} eventId - Event ID for validation
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  static async validateCoupon(couponCode, subtotal, userId, eventId) {
    try {
      // Get all coupons from API (since backend doesn't support generic single lookup by code yet)
      // and filter locally.
      const response = await ApiService.getCoupons();
      // Response is expected to be { data: { ...coupons... } } or similar based on backend.
      // Assuming response payload structure. The documentation says: Returns Future<ApiResponse<Map<String, dynamic>>>
      // Usually that means { success: true, data: { ... } } or just the data map?
      // "getCoupons" returns "all coupons".
      // Let's assume response.data is the list or map of coupons.
      // If it returns a map where key is ID (or code?), we check.

      const couponsData = response.data || response; // Fallback

      let coupon = null;

      // Check if couponsData is array or map
      if (Array.isArray(couponsData)) {
        coupon = couponsData.find(c => c.code === couponCode.toUpperCase());
      } else if (typeof couponsData === 'object') {
        // Maybe keyed by ID? Or Code?
        // Iterating to find matching code
        const couponKey = Object.keys(couponsData).find(key =>
          couponsData[key].code === couponCode.toUpperCase() || key === couponCode.toUpperCase()
        );
        if (couponKey) coupon = couponsData[couponKey];
      }

      if (!coupon) {
        return {
          success: false,
          error: 'Invalid coupon code'
        };
      }

      // Check if coupon is active
      if (!coupon.isActive) {
        return {
          success: false,
          error: 'This coupon is no longer active'
        };
      }

      // Check if coupon has expired
      const now = new Date();
      // Handle different date formats (string or timestamp)
      const expiresAt = new Date(coupon.expiresAt); // API usually returns generic date string
      if (now > expiresAt) {
        return {
          success: false,
          error: 'This coupon has expired'
        };
      }

      // Check if coupon has reached max usage
      if (coupon.usedCount >= coupon.maxUsage) {
        return {
          success: false,
          error: 'This coupon has reached its usage limit'
        };
      }

      // Check minimum purchase amount
      if (subtotal < coupon.minPurchaseAmount) {
        return {
          success: false,
          error: `Minimum purchase amount of ₹${coupon.minPurchaseAmount} required`
        };
      }

      // Check if coupon is assigned to specific users
      if (coupon.assignedToUserIds && !coupon.assignedToUserIds.includes(userId)) {
        return {
          success: false,
          error: 'This coupon is not available for your account'
        };
      }

      // Check if coupon is applicable to this event
      if (coupon.applicableEventIds && !coupon.applicableEventIds.includes(eventId)) {
        return {
          success: false,
          error: 'This coupon is not applicable to this event'
        };
      }

      // Calculate discount amount
      let discountAmount = 0;
      if (coupon.discountType === 'percentage') {
        discountAmount = (subtotal * coupon.discountValue) / 100;
        // Apply max discount limit for percentage coupons
        if (discountAmount > coupon.maxDiscountAmount) {
          discountAmount = coupon.maxDiscountAmount;
        }
      } else if (coupon.discountType === 'fixedAmount') {
        discountAmount = coupon.discountValue;
      }

      // Ensure discount doesn't exceed subtotal
      discountAmount = Math.min(discountAmount, subtotal);

      return {
        success: true,
        data: {
          couponCode: coupon.code,
          discountType: coupon.discountType,
          discountValue: coupon.discountValue,
          discountAmount: Math.round(discountAmount * 100) / 100, // Round to 2 decimal places
          originalSubtotal: subtotal,
          newSubtotal: Math.round((subtotal - discountAmount) * 100) / 100,
          couponId: coupon.id
        }
      };

    } catch (error) {
      console.error('Error validating coupon:', error);
      return {
        success: false,
        error: 'Failed to validate coupon. Please try again.'
      };
    }
  }

  /**
   * Apply coupon usage (increment usage count)
   * @param {string} couponCode - The coupon code
   * @returns {Promise<boolean>}
   */
  static async applyCouponUsage(couponCode) {
    // API doesn't support direct usage increment from client.
    // This should be handled by the backend when creating a booking.
    console.warn('applyCouponUsage called - this should be handled by backend createBooking');
    return true;
  }

  /**
   * Format coupon display text
   * @param {object} couponData - Coupon data from validation
   * @returns {string}
   */
  static formatCouponDisplay(couponData) {
    if (couponData.discountType === 'percentage') {
      return `${couponData.discountValue}% OFF`;
    } else {
      return `₹${couponData.discountValue} OFF`;
    }
  }

  /**
   * Check if user has reached per-user usage limit
   * @param {string} couponCode - The coupon code
   * @param {string} userId - User ID
   * @returns {Promise<boolean>}
   */
  static async checkUserUsageLimit(couponCode, userId) {
    try {
      // This would require tracking user coupon usage in a separate collection
      // For now, we'll assume it's handled by the main validation
      return true;
    } catch (error) {
      console.error('Error checking user usage limit:', error);
      return false;
    }
  }
}
// Coupon validation rules
export const COUPON_VALIDATION_RULES = {
  MIN_CODE_LENGTH: 3,
  MAX_CODE_LENGTH: 20,
  ALLOWED_CHARACTERS: /^[A-Z0-9]+$/
};

// Helper function to validate coupon code format
export const validateCouponCodeFormat = (code) => {
  if (!code || typeof code !== 'string') {
    return { valid: false, error: 'Coupon code is required' };
  }

  const trimmedCode = code.trim().toUpperCase();

  if (trimmedCode.length < COUPON_VALIDATION_RULES.MIN_CODE_LENGTH) {
    return { valid: false, error: 'Coupon code is too short' };
  }

  if (trimmedCode.length > COUPON_VALIDATION_RULES.MAX_CODE_LENGTH) {
    return { valid: false, error: 'Coupon code is too long' };
  }

  if (!COUPON_VALIDATION_RULES.ALLOWED_CHARACTERS.test(trimmedCode)) {
    return { valid: false, error: 'Coupon code can only contain letters and numbers' };
  }

  return { valid: true, formattedCode: trimmedCode };
};
